/**
 * Pure, DOM-free renderer for the page-transition "O" logo draw.
 *
 * `LogoLoop` works out each frame's state from elapsed time (draw, spin,
 * finish) and `drawLogoFrame` paints it, so the same code runs in the
 * OffscreenCanvas worker (`logoWorker.ts`) and in the main-thread
 * fallback (`Logo.tsx`).
 *
 * Timeline (all from `animationConfig.pageTransition`):
 *  1. Guide outline fades in, a faint ghost of the final shape, so the
 *     user has a frame of reference for what the stroke draws toward.
 *  2. Logo scales up (once) as the spinner starts: the head draws out an
 *     arc and the tail follows the head's exact motion a moment later,
 *     so both ends move at one speed and the arc keeps its length.
 *  3. Page ready (`finish()`): the tail eases to a stop and the head,
 *     still at the same speed, carries on round to close the outline.
 *  4. Only once the outline is closed (and so the page is ready) does
 *     the fill fade in, and the guide out: the last step before the
 *     curtains open.
 *
 * The motion is deterministic, so `finish()` works out there and then
 * when the logo will rest (`restsInMs`); the counter paces its run to
 * 100 to land at that moment (NumberedLoader's `finishAtMs`).
 *
 * The stroke is always a window [tail, tail + len] along each subpath,
 * as a fraction of that subpath, so the ring and leaf move in step.
 */
import { animationConfig, easingFns } from "../../config/animation";

const PT = animationConfig.pageTransition;

export type LogoTheme = { stroke: string; fill: string };

export const LOGO_PATH_D =
    "M121.72 60.8603C121.72 94.3503 94.5198 121.72 60.8598 121.72C27.1998 121.72 -0.000195192 94.6904 -0.000195192 60.8603C-0.000195192 27.3703 27.0298 0.000350833 60.6898 0.000350833C94.3498 0.000350833 121.72 27.2003 121.72 60.8603ZM98.0898 86.1904C99.2798 54.2303 73.9498 6.46035 45.8998 5.44034C31.1098 4.93035 24.1398 19.3803 23.6298 35.7003C22.4398 67.8303 47.5998 115.43 75.8198 116.28C90.4398 116.79 97.5798 102.51 98.0898 86.1904Z";

/** Outer ring and inner leaf, stroked separately so each gets its own
 *  dash. Both move through the same fraction of themselves at once, so
 *  they draw and spin in step. */
export const LOGO_SUBPATHS_D = LOGO_PATH_D.split(/(?=M)/);

/** Logo box at scale 1 (CSS px). The 160px artwork sits centred in it,
 *  the same as the old `w-[200px] p-5` container around the SVG. */
export const LOGO_BOX = 200;
const ART = 160;
const VIEWBOX = { x: -4, y: -4, w: 133, h: 136 };
/** viewBox → art box, `preserveAspectRatio="xMidYMid meet"`. */
const VB_SCALE = Math.min(ART / VIEWBOX.w, ART / VIEWBOX.h);
const VB_OFFSET_X = (LOGO_BOX - VIEWBOX.w * VB_SCALE) / 2 - VIEWBOX.x * VB_SCALE;
const VB_OFFSET_Y = (LOGO_BOX - VIEWBOX.h * VB_SCALE) / 2 - VIEWBOX.y * VB_SCALE;

const SPIN = PT.logoSpin;

// Timeline, in seconds from start.
const HEAD_START = PT.logoGuideIn.durationS;
const SPEED = SPIN.speedRevPerS;
const RAMP_S = SPIN.rampS;
/** The tail sets off this long after the head, so the arc is `arcLength`. */
const TAIL_DELAY = SPIN.arcLength / SPEED;
const SETTLE_S = Math.max(PT.logoFillIn.durationS, PT.logoGuideOut.durationS);
/** Bound on how long the outline takes to close once `finish()` is
 *  called: the head going once round from the very start, at worst. */
const CLOSE_BOUND_S = HEAD_START + RAMP_S + 1 / SPEED;
/** Bound on how long the logo takes to come to rest once `finish()` is
 *  called: the outline closing, then the fill. */
export const LOGO_TOTAL_MS = (CLOSE_BOUND_S + SETTLE_S) * 1000;

/** `performance.now()` counts from each thread's own origin, so a time
 *  crosses between the logo's worker and the page as an epoch time. */
export const toEpochMs = (localMs: number) => performance.timeOrigin + localMs;
export const fromEpochMs = (epochMs: number) => epochMs - performance.timeOrigin;

/** Eased 0→1 progress of a segment starting at `start` lasting `dur`. */
const seg = (t: number, start: number, dur: number, ease: (x: number) => number) =>
    ease(Math.min(1, Math.max(0, (t - start) / dur)));

/** The first `t` in [lo, hi] at which the non-decreasing `f` reaches 0,
 *  by bisection (`f(lo) < 0 <= f(hi)`); to well under a microsecond. */
const crossing = (f: (t: number) => number, lo: number, hi: number) => {
    for (let i = 0; i < 48; i++) {
        const mid = (lo + hi) / 2;
        if (f(mid) >= 0) hi = mid;
        else lo = mid;
    }
    return hi;
};

// ── Stroke kinematics (positions in revolutions of each subpath) ─────

/** Where an end is `s` seconds after setting off from 0: an even ramp up
 *  to `SPEED` over `RAMP_S`, then steady. */
const travelled = (s: number) => {
    if (s <= 0) return 0;
    if (s < RAMP_S) return (SPEED * s * s) / (2 * RAMP_S);
    return SPEED * (s - RAMP_S / 2);
};
const speedAt = (s: number) => (s <= 0 ? 0 : SPEED * Math.min(1, s / RAMP_S));

const head = (t: number) => travelled(t - HEAD_START);
/** The tail repeats the head's motion, `TAIL_DELAY` later. */
const tail = (t: number) => travelled(t - HEAD_START - TAIL_DELAY);

const pathCache = new Map<string, Path2D>();
const path2d = (d: string) => {
    let p = pathCache.get(d);
    if (!p) pathCache.set(d, (p = new Path2D(d)));
    return p;
};

export type LogoFrameParams = {
    /** Length of each entry in `LOGO_SUBPATHS_D`, in viewBox units. */
    subpathLengths: number[];
    theme: LogoTheme;
    /** Canvas size in CSS px. The logo box fills it at `endScale`. */
    cssSize: number;
    dpr: number;
    endScale: number;
};

/** One frame of the logo, as worked out by `LogoLoop`. */
export type LogoFrameState = {
    /** 0→1 progress of the logo's one-off scale-up. */
    grow: number;
    guide: number;
    fill: number;
    /** Visible stroke on each subpath: from `tail` (revolutions,
     *  unbounded) for `len` (0–1 of the subpath; 1 is the whole outline). */
    stroke: { tail: number; len: number };
};

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export function drawLogoFrame(ctx: Ctx, f: LogoFrameState, p: LogoFrameParams) {
    const startScale = PT.logoContainerScale.startScale;
    const scale = startScale + (p.endScale - startScale) * f.grow;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // CSS px → device px, scale about the canvas centre, then box → viewBox.
    ctx.setTransform(p.dpr, 0, 0, p.dpr, 0, 0);
    ctx.translate(p.cssSize / 2, p.cssSize / 2);
    ctx.scale(scale, scale);
    ctx.translate(-LOGO_BOX / 2 + VB_OFFSET_X, -LOGO_BOX / 2 + VB_OFFSET_Y);
    ctx.scale(VB_SCALE, VB_SCALE);

    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = p.theme.stroke;
    ctx.fillStyle = p.theme.fill;

    // Guide: hairline that doesn't scale with the logo.
    if (f.guide > 0) {
        ctx.globalAlpha = f.guide;
        ctx.lineWidth = 0.5 / (VB_SCALE * scale);
        ctx.setLineDash([]);
        ctx.stroke(path2d(LOGO_PATH_D));
    }

    // Stroke window. The dash period is exactly the subpath's length, so
    // a window crossing the path's start point stays in one piece.
    const { tail, len } = f.stroke;
    if (len > 0) {
        ctx.globalAlpha = 1;
        ctx.lineWidth = 2;
        LOGO_SUBPATHS_D.forEach((d, i) => {
            const L = p.subpathLengths[i];
            if (len >= 1) ctx.setLineDash([]);
            else {
                ctx.setLineDash([len * L, (1 - len) * L]);
                ctx.lineDashOffset = -(tail - Math.floor(tail)) * L;
            }
            ctx.stroke(path2d(d));
        });
        ctx.lineDashOffset = 0;
    }

    if (f.fill > 0) {
        ctx.globalAlpha = f.fill;
        ctx.fill(path2d(LOGO_PATH_D));
    }
    ctx.globalAlpha = 1;
}

/**
 * The logo's playback while the page loads: a spinner whose two ends move
 * at one speed, until `finish()` stops the tail and the head closes the
 * outline. Positions and speeds are continuous throughout.
 */
export class LogoLoop {
    private t = 0;
    /** Tail's position and speed when `finish()` came; it brakes from there. */
    private stop: { at: number; tail: number; speed: number } | null = null;
    /** When the outline closes: worked out by `finish()`, so it may still
     *  lie ahead. The fill runs from that moment. */
    private closedAt: number | null = null;
    /** True once it has come to rest fully formed after `finish()`. */
    done = false;

    finish() {
        if (this.stop) return;
        const s = this.t - HEAD_START - TAIL_DELAY;
        this.stop = { at: this.t, tail: tail(this.t), speed: speedAt(s) };
        // The head is never slower than the tail (the tail copies its
        // motion late, then brakes), so its lead only grows and reaches
        // a full turn once: the exact moment the outline closes.
        this.closedAt = crossing((t) => head(t) - this.tailAt(t) - 1, this.t, this.t + CLOSE_BOUND_S);
    }

    advance(dtMs: number) {
        this.t += dtMs / 1000;
        if (this.closedAt !== null) this.done = this.t >= this.closedAt + SETTLE_S;
    }

    /** Once `finish()` has been called: how long (ms) from now until it
     *  rests fully formed. */
    get restsInMs(): number | null {
        if (this.closedAt === null) return null;
        return Math.max(0, (this.closedAt + SETTLE_S - this.t) * 1000);
    }

    /** Tail position at `t`: following the head, or braking evenly (at
     *  the same rate it ramped up) to rest after `finish()`. */
    private tailAt(t: number) {
        if (!this.stop) return tail(t);
        const { at, tail: from, speed } = this.stop;
        const brake = SPEED / RAMP_S;
        const s = Math.min(t - at, speed / brake);
        return from + speed * s - (brake * s * s) / 2;
    }

    get state(): LogoFrameState {
        const t = this.t;
        const fillStart = this.closedAt;
        const closed = fillStart !== null && t >= fillStart;
        const tailAt = this.tailAt(t);
        return {
            grow: seg(t, HEAD_START, PT.logoContainerScale.durationS, easingFns.ui),
            guide: PT.guideOpacity * (fillStart === null || t < fillStart
                ? seg(t, 0, PT.logoGuideIn.durationS, easingFns.ui)
                : 1 - seg(t, fillStart, PT.logoGuideOut.durationS, easingFns.ui)),
            fill: fillStart === null ? 0 : seg(t, fillStart, PT.logoFillIn.durationS, easingFns.ui),
            stroke: closed
                ? { tail: 0, len: 1 }
                : { tail: tailAt, len: Math.min(1, head(t) - tailAt) },
        };
    }
}

/** Wipes the canvas (between transitions). */
export function clearLogo(ctx: Ctx) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
}
